import React from 'react';
import {InvariantError} from 'ts-invariant';
import {stringify} from 'qs';
import {useQuery, useQueryClient} from '@tanstack/react-query';
import {
  UserAuth,
  tokenToAuth,
  decodeJwt,
} from '@practicegenius/domain/src/data/users/UserAuth';
import {
  makeNoAuthApiClient,
  makeAuthApiClient,
} from '@practicegenius/http-clients-js/src/ApiClient';
import {ApiClient} from '@practicegenius/http-clients-js/src/types';
import {
  DecodedGlobalUserJwt,
  decodeGlobalUserJwt,
} from '@practicegenius/domain/src/data/users/globalUserJwt';
import {setUserInfoInWindowRollbar} from '@practicegenius/next-utils/src/nextLogger';
import {formatUserIdForTelemetry} from '@practicegenius/domain/src/data/users/telemetry';
import {CSRF_HEADER_NAME} from '@practicegenius/next-utils/src/server/headers';
import {PG_COMM_URL, PG_CORE_URL} from '../constants/env';
import logger from '../utils/logger';

/** HTTP Client for requests to sites-dot's backend (Kohana at the moment)
 * These requests are implicitly authenticated through cookies
 *   so the client does not need extra setup
 * @deprecated call useCsrfApiClient instead
 */
export const sitesHttpClient = makeNoAuthApiClient({
  headers: {
    'Content-Type': 'application/x-www-form-urlencoded',
    'X-Requested-With': 'xmlhttprequest',
  },
  transformRequest: [data => stringify(data)],
});

function requestJwt() {
  return sitesHttpClient._unsafe.post('/api/token').then(({data}) => {
    return data.token as string;
  });
}

function requestGlobalJwt() {
  return sitesHttpClient._unsafe.post('/api/global_token').then(({data}) => {
    const token = data.token as string;
    return token;
  });
}

type ApiContextValue = {
  /** Practice User JWT */
  practiceUserJwt: string;
  /** Global User JWT */
  globalUserJwt: string;
  /** Core API authenticated as business user */
  coreAuth: ApiClient;
  /** Communications API authenticated as business user */
  commsAuth: ApiClient;
  /** Decoded version of the practice-user core jwt */
  decodedToken: UserAuth;
  /** Decoded version of the global-user core jwt */
  decodedGlobalUserToken: DecodedGlobalUserJwt;
  /** Core API authenticated as global user */
  globalUserCoreAuth: ApiClient;
};
const ApiContext = React.createContext<ApiContextValue | null>(null);

const makeApiContext = (practiceUserJwt: string, globalUserJwt: string) => {
  return {
    practiceUserJwt,
    globalUserJwt,
    coreAuth: makeAuthApiClient({
      baseURL: PG_CORE_URL,
      authToken: practiceUserJwt,
    }),
    commsAuth: makeAuthApiClient({
      baseURL: PG_COMM_URL,
      authToken: practiceUserJwt,
    }),
    globalUserCoreAuth: makeAuthApiClient({
      baseURL: PG_CORE_URL,
      authToken: globalUserJwt,
    }),
    decodedGlobalUserToken: decodeGlobalUserJwt(globalUserJwt),
    decodedToken: tokenToAuth(decodeJwt(practiceUserJwt)),
  };
};

/** The actual useQuery that hydrates the API client context. Not intended to be used externally */
const useApiClients = (initialJwts?: {
  initialPracticeUserJwt: string;
  initialGlobalUserJwt: string;
}) => {
  const initialData =
    initialJwts?.initialPracticeUserJwt && initialJwts?.initialGlobalUserJwt
      ? makeApiContext(
          initialJwts.initialPracticeUserJwt,
          initialJwts.initialGlobalUserJwt,
        )
      : undefined;

  return useQuery({
    queryKey: ['apiContext'],
    queryFn: () =>
      Promise.all([requestJwt(), requestGlobalJwt()]).then(
        ([coreJwt, globalUserJwt]) => {
          return makeApiContext(coreJwt, globalUserJwt);
        },
      ),
    initialData,
    // Fetch an updated JWT every 5 minutes
    staleTime: 1000 * 60 * 5,
  });
};

export const useInvalidateApiContext = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({queryKey: ['apiContext']});
};

/** Use the collection of api clients needed to make authenticated requests to api-core */
export const useApi = (): ApiContextValue => {
  const {data} = useApiClients();
  if (!data)
    throw new InvariantError(
      "useApi must be called inside ApiProvider's context",
    );

  return data;
};

/** If the app is authenticated, this returns ApiContextValue, otherwise undefined */
export const useMaybeApi = (): ApiContextValue | null =>
  React.useContext(ApiContext);

export const AuthApiProvider: React.FCC<{
  initialJwts?: {
    initialPracticeUserJwt: string;
    initialGlobalUserJwt: string;
  };
  csrf?: string;
}> = ({children, initialJwts, csrf}) => {
  // if a csrf is provided, add it to the sites http client
  if (csrf) {
    sitesHttpClient._unsafe.defaults.headers[CSRF_HEADER_NAME] = csrf;
  }
  const apiClientQuery = useApiClients(initialJwts);

  React.useEffect(() => {
    if (!apiClientQuery.data) return;
    const formattedUserId = formatUserIdForTelemetry(
      apiClientQuery.data.decodedToken.userId,
      'practice',
    );
    setUserInfoInWindowRollbar(formattedUserId);
    logger.debug(`Set user info for ${formattedUserId} in rollbar`);
  }, [apiClientQuery.data]);

  switch (apiClientQuery.status) {
    case 'error':
      // Just rendering children when the token request(s) fail
      // just defers the error down to the components, as the first time they try to use the
      // ApiClient, we'll get an error because the default client's token is invalid~
      // TODO We need an error component here like we have in my-dot
      return <>{children}</>;

    case 'success':
      return (
        <ApiContext.Provider value={apiClientQuery.data}>
          {children}
        </ApiContext.Provider>
      );

    // Not showing anything here gives this parity with the old http clients 😅
    default:
      return null;
  }
};

/** Wraps a component with the api clients from the context, deferring rendering until the api clients are available
 * @deprecated this should not be used in new function components.
 */
export const withApiClients =
  (Inner: React.ComponentType<any>) => (props: any) => {
    const apiClientQuery = useApiClients();

    switch (apiClientQuery.status) {
      case 'error':
        // Just rendering children when the token request(s) fail
        // just defers the error down to the components, as the first time they try to use the
        // ApiClient, we'll get an error because the default client's token is invalid
        // TODO We need an error component here like we have in my-dot
        return <Inner {...props} />;

      case 'success':
        return (
          <ApiContext.Provider value={apiClientQuery.data}>
            <Inner {...props} authApiData={apiClientQuery.data} />
          </ApiContext.Provider>
        );

      // Not showing anything here gives this parity with the old http clients 😅
      default:
        return null;
    }
  };

/** @deprecated this is only exported for legacy use-cases */
export type WithApiClientsAuthApiDataProp = ApiContextValue;

// @see apps/sites.patientrewardshub.com/application/classes/AuthLevel.php
export type GlobalUserAuthLevel = 'basic' | 'enhanced';

/**
 * Get the auth level of the global user based on the scopes in the decoded global user token
 * Currently the global user token scopes do not provide granular permissions so any scope
 *   present can be interpreted as enhanced security. Once we have more granular permissions,
 *   then this whole function will need to be replaced.
 */
export const useGlobalUserAuthLevel = (): GlobalUserAuthLevel => {
  const {decodedGlobalUserToken} = useApi();
  if (decodedGlobalUserToken.scopes.length > 0) {
    return 'enhanced';
  }
  return 'basic';
};
